// AssemblyInstructionsModal.jsx — "Surinkimo instrukcija" (assembly instructions)
// PDF for a single furniture item. Used both from /admin/furniture AND directly
// on the public /baldai/:code page (this component has no admin-only logic —
// GET /furnitures/:code is a public endpoint — so it lives here, not under
// src/admin/, now that anyone can open it). Reuses the exact same battle-tested
// pipeline as the configurator's "Suvestinė" (order summary) PDF:
// useFurnitureDrawings for off-screen 3D/2D capture, calculatePrice for the
// per-door panel list and hardware counts, and exportSummaryToPdf
// (html2canvas → jsPDF) for output.
//
// Honest scope note: this produces a specification/reference document — exact
// door panel dimensions, materials, hardware checklist, and the existing 3D/2D
// drawings — plus a generic numbered assembly order. It does NOT generate
// literal per-step exploded-view diagrams (IKEA-style pictograms); that would
// require an entirely new geometry-diagram renderer, a separate, much larger
// undertaking than reusing this existing drawing/BOM pipeline. The 3D capture's
// own white margin around the product (from Builder3D's shared camera framing)
// is also left as-is here — tightening it touches the same camera-fit logic
// used by the live configurator and Interactive3DViewer, too risky to change
// as a side effect of this document's layout.
//
// A single /baldai/:code record is always ONE purchasable product even when
// it's internally built from several `furnitureId` cabinet modules side by
// side (same reasoning as FurnitureDrawingTabs.jsx's buildCombinedGroup) — so
// unlike Suvestinė (a multi-item project/order summary, where several REAL
// separate "Baldas 1 / Baldas 2" purchases legitimately need separate
// sections), this document always treats the whole `furniture.cabinets` array
// as one combined unit and never shows that internal grouping to the reader.
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { makeStyles, tokens, Button, Spinner, FluentProvider } from '@fluentui/react-components';
import { API_BASE, API_HOST } from '../../config.js';
import { calculatePrice } from '../../configurator/price/calculatePrice';
import useFurnitureDrawings from '../../configurator/summary/useFurnitureDrawings';
import { exportSummaryToPdf } from '../../configurator/summary/pdfExport';
import { iconMap } from '../../theme/icons';
import { fititLightTheme } from '../../theme/brand';
import {
    resolvePlateImage, resolveUploadImage,
    getBasisImage, getBasisLabel, getEdgesImage, getEdgesLabel,
    getHandleTypeImage, getHandleTypeLabel, getDoorTypeLabel,
    getInteriorItemImage, getInteriorItemLabel,
} from '../../configurator/summary/materialThumbnails';

const ConstructionIcon = iconMap.construction;
const DownloadIcon = iconMap.download;
const DismissIcon = iconMap.close;

// Šio failo apatinis "spausdinimo turinys" (PageShell/TitleBlock/H2/ThumbRow/
// IconGlyph/ToolsRow/DrawingPair/HardwareList žemiau) SĄMONINGAI naudoja
// literalius `style={{...}}` objektus, NE Tailwind ar Fluent token'us — tai
// html2canvas fiksuojamas A4 puslapio turinys (žr. failo viršuje esantį
// komentarą), kur reikalingi tikslūs mm/px dydžiai, nepriklausomi nuo
// vartotojo pasirinktos šviesios/tamsios temos. Konvertuojama čia — TIK
// aplinkinis modalo rėmas (antraštė, mygtukai, portalas), kuris realiai
// naudojo Tailwind className.
const useStyles = makeStyles({
    root: { position: 'fixed', inset: 0, zIndex: tokens.zIndexOverlay, display: 'flex', flexDirection: 'column', backgroundColor: tokens.colorNeutralBackground3 },
    header: {
        display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM,
        paddingLeft: tokens.spacingHorizontalL, paddingRight: tokens.spacingHorizontalL,
        height: '64px', backgroundColor: tokens.colorNeutralBackground1,
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, flexShrink: 0,
        '@media (min-width: 640px)': { paddingLeft: tokens.spacingHorizontalXXL, paddingRight: tokens.spacingHorizontalXXL },
    },
    iconChip: {
        width: '36px', height: '36px', borderRadius: tokens.borderRadiusXLarge,
        backgroundColor: tokens.colorBrandBackground2, color: tokens.colorBrandForeground2,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    },
    titleWrap: { minWidth: 0 },
    title: {
        fontSize: tokens.fontSizeBase300, fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', margin: 0,
    },
    progressText: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3, margin: 0 },
    errorText: { fontSize: tokens.fontSizeBase200, color: tokens.colorPaletteRedForeground1, margin: 0 },
    headerActions: { marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS },
    body: { flex: 1, overflowY: 'auto', paddingTop: tokens.spacingVerticalXXL, paddingBottom: tokens.spacingVerticalXXL },
    loadingWrap: { display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: '6rem', paddingBottom: '6rem' },
});

// Baldo gabaritai skaičiuojami iš viso cabinets masyvo apgaubiančio stačiakampio
// — TA PATI logika, kaip FurnitureDrawingTabs.jsx buildCombinedGroup.
const buildCombinedGroup = (cabinets = []) => {
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity, depth = 0;
    cabinets.forEach((cab) => {
        const x = Number(cab.x) || 0; const y = Number(cab.y) || 0;
        const w = Number(cab.width) || 0; const h = Number(cab.height) || 0;
        minX = Math.min(minX, x); maxX = Math.max(maxX, x + w);
        minY = Math.min(minY, y); maxY = Math.max(maxY, y + h);
        depth = Math.max(depth, Number(cab.depth) || 0);
    });
    if (!isFinite(minX)) return null;
    return {
        furnitureId: 'combined', cabinets,
        x: minX, y: minY, width: maxX - minX, height: maxY - minY, depth,
    };
};

const QUALITY_KEYS = ['quality_0', 'quality_1', 'quality_2'];
const TEAL = '#09aa89';

// Mirrors SummaryView's buildInteriorItems — sectionOptions values are either
// a plain type int or an array of {type,height} (merged sections).
const buildInteriorItems = (scopedSectionOptions) => {
    const items = [];
    Object.entries(scopedSectionOptions || {}).forEach(([key, val]) => {
        if (Array.isArray(val)) {
            val.forEach((v, i) => { if (v?.type) items.push({ key: `${key}-${i}`, type: v.type, heightCm: v.height }); });
        } else if (val) {
            items.push({ key, type: val, heightCm: null });
        }
    });
    return items;
};

// Kiekvienas žingsnis rodomas TIK jei šis konkretus baldas realiai turi tą
// elementą — anksčiau visi 8 žingsniai rodydavosi visada, net kai baldas,
// pvz., neturėjo nė vieno stalčiaus ar durelių ("jei yra" formuluotė
// tekste to nekompensuoja, žingsnis vis tiek liko sąraše kaip triukšmas).
const ASSEMBLY_STEPS = [
    { key: 'step_0' },
    { key: 'step_1' },
    { key: 'step_2', when: (ctx) => ctx.hasShelves || ctx.hasInteriorItems },
    { key: 'step_3', when: (ctx) => ctx.hasDrawers },
    { key: 'step_4', when: (ctx) => ctx.hasDoors },
    { key: 'step_5', when: (ctx) => ctx.hasDoors },
    { key: 'step_6', when: (ctx) => ctx.hasBase },
    { key: 'step_7', when: (ctx) => ctx.isTallOrWallMounted },
];

const PageShell = ({ children }) => (
    <div className="fitit-summary-page" style={{ width: '210mm', minHeight: '297mm', margin: '0 auto 16px', padding: '14mm', background: '#fff', boxSizing: 'border-box', fontFamily: 'Arial, Helvetica, sans-serif', color: '#222' }}>
        {children}
    </div>
);

const TitleBlock = ({ code, pageLabel, dateStr, pageNum, totalPages }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', borderBottom: `2px solid ${TEAL}`, paddingBottom: 8, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <span style={{ fontWeight: 800, fontSize: 18, color: TEAL }}>fitit.lt</span>
            <span style={{ fontSize: 13, color: '#555' }}>Baldo kodas: <strong>{code}</strong></span>
        </div>
        <div style={{ textAlign: 'right' }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#222' }}>{pageLabel}</div>
            <div style={{ fontSize: 11, color: '#999' }}>{dateStr}{pageNum ? ` · ${pageNum} / ${totalPages}` : ''}</div>
        </div>
    </div>
);

const H2 = ({ children }) => <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>{children}</div>;

const ThumbRow = ({ image, title, sub }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {image && (
            <div style={{ width: 42, height: 42, borderRadius: 6, overflow: 'hidden', border: '1px solid #e0e0e0', flexShrink: 0, background: '#fafafa' }}>
                <img src={image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
        )}
        <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 13, lineHeight: 1.25 }}>{title}</div>
            {sub && <div style={{ fontSize: 11.5, color: '#777' }}>{sub}</div>}
        </div>
    </div>
);

// Paprasti vektoriniai SVG kontūrai (NE Material Symbols šriftas): html2canvas
// (naudojamas šio dokumento PDF eksportui, žr. pdfExport.js) nepatikimai
// atvaizduoja šriftinių piktogramų ligatūras, o SVG rastrizuoja teisingai
// tiek ekrane, tiek eksportuotame PDF'e.
const ICON_PATHS = {
    screwdriver: <><rect x="9" y="2" width="6" height="7" rx="1.5" /><line x1="12" y1="9" x2="12" y2="18" /><path d="M8.5 18 L12 21.5 L15.5 18 Z" /></>,
    drill: <><rect x="3" y="9" width="12" height="6" rx="1.5" /><path d="M7 15 L5.5 21 L9.5 21 L9 15 Z" /><rect x="15" y="10.5" width="6" height="3" /></>,
    level: <><rect x="2" y="10" width="20" height="4" rx="1" /><circle cx="12" cy="12" r="1.4" /><line x1="7" y1="10" x2="7" y2="14" /><line x1="17" y1="10" x2="17" y2="14" /></>,
    door: <><rect x="6" y="3" width="12" height="18" rx="1" /><circle cx="15" cy="12" r="1" /></>,
    drawer: <><rect x="4" y="4" width="16" height="7" rx="1" /><line x1="10" y1="7.5" x2="14" y2="7.5" /><rect x="4" y="13" width="16" height="7" rx="1" /><line x1="10" y1="16.5" x2="14" y2="16.5" /></>,
    liftUp: <><circle cx="12" cy="12" r="9" /><polyline points="8.5,13 12,9 15.5,13" /><line x1="12" y1="9" x2="12" y2="16" /></>,
    liftDown: <><circle cx="12" cy="12" r="9" /><polyline points="8.5,11 12,15 15.5,11" /><line x1="12" y1="8" x2="12" y2="15" /></>,
    hanger: <><circle cx="12" cy="5" r="1.4" /><path d="M12 6.5 V8" /><path d="M4 16 L12 9 L20 16" /><line x1="3" y1="16" x2="21" y2="16" /></>,
    pullout: <><rect x="3" y="8" width="11" height="9" rx="1" /><line x1="13" y1="12.5" x2="21" y2="12.5" /><polyline points="17.5,9 21,12.5 17.5,16" /></>,
    pantograph: <><polyline points="6,10 12,6 18,10" /><polyline points="6,17 12,13 18,17" /></>,
    legs: <><rect x="9" y="3" width="6" height="6" rx="1" /><line x1="12" y1="9" x2="12" y2="19" /><line x1="8" y1="19" x2="16" y2="19" /></>,
    hardware: <><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="2.4" /></>,
};

const IconGlyph = ({ name, size = 18, color = '#444' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        {ICON_PATHS[name] || ICON_PATHS.hardware}
    </svg>
);

const REQUIRED_TOOLS = [
    { icon: 'screwdriver', key: 'tool_screwdriver' },
    { icon: 'drill', key: 'tool_drill' },
    { icon: 'level', key: 'tool_level' },
];

const ToolsRow = () => {
    const { t } = useTranslation();
    return (
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', padding: '12px 16px', border: '1px solid #eee', borderRadius: 8, marginBottom: 20, background: '#fafafa' }}>
            {REQUIRED_TOOLS.map((tool) => (
                <div key={tool.icon} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 34, height: 34, borderRadius: tokens.borderRadiusCircular, background: '#fff', border: '1px solid #ddd', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <IconGlyph name={tool.icon} size={18} color={TEAL} />
                    </div>
                    <span style={{ fontSize: 12.5, color: '#333', fontWeight: 600 }}>{t(`shop.assembly_instructions.${tool.key}`)}</span>
                </div>
            ))}
        </div>
    );
};

// Fiksuotas (ne minHeight) dėžutės aukštis + object-fit:contain — anksčiau
// dėžutė augdavo pagal paveikslėlio natūralią proporciją (width:100%, be
// aukščio ribos), todėl "Bendra informacija"/"Fasadas / Durelės" puslapiai
// realiai išaugdavo iki 333–367mm vietoj A4 297mm, o pdfExport.js juos
// proporcingai sutraukdavo eksportuojant — tekstas/brėžiniai tuose
// puslapiuose PDF'e išeidavo pastebimai smulkesni nei kituose. Fiksuotas
// aukštis garantuoja nuspėjamą puslapio dydį nepriklausomai nuo konkretaus
// baldo brėžinio proporcijų.
const DRAWING_BOX_H = 232;

const DrawingPair = ({ img3d, img2d, dimensions }) => {
    const { t } = useTranslation();
    const dimLabel = dimensions
        ? `${(dimensions[0] * 100).toFixed(0)} × ${(dimensions[1] * 100).toFixed(0)} × ${(dimensions[2] * 100).toFixed(0)} cm (P×A×G)`
        : null;
    const imgStyle = { maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto', objectFit: 'contain', display: 'block' };
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 18 }}>
            <div style={{ position: 'relative', border: '1px solid #ddd', borderRadius: 6, overflow: 'hidden', background: '#fafafa', height: DRAWING_BOX_H, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {dimLabel && (
                    <div style={{ position: 'absolute', top: 8, left: 8, background: 'rgba(11,11,11,0.72)', color: '#fff', padding: '4px 8px', borderRadius: 5, fontSize: 11, fontWeight: 600 }}>
                        {dimLabel}
                    </div>
                )}
                {img3d && <img src={img3d} alt={t('shop.assembly_instructions.alt_3d')} style={imgStyle} />}
            </div>
            <div style={{ border: '1px solid #ddd', borderRadius: 6, overflow: 'hidden', background: '#fafafa', height: DRAWING_BOX_H, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {img2d && <img src={img2d} alt={t('shop.assembly_instructions.alt_drawing')} style={imgStyle} />}
            </div>
        </div>
    );
};

const HardwareList = ({ rows }) => {
    const { t } = useTranslation();
    return (
        <div style={{ border: '1px solid #eee', borderRadius: 8, overflow: 'hidden' }}>
            {rows.map((r, i) => (
                <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 14px', borderTop: i === 0 ? 'none' : '1px solid #f0f0f0', background: i % 2 ? '#fafafa' : '#fff' }}>
                    <div style={{ width: 30, height: 30, borderRadius: 7, background: '#f3f3f3', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <IconGlyph name={r.icon} size={16} color="#555" />
                    </div>
                    <span style={{ fontSize: 12.5, color: '#333', flex: 1 }}>{r.label}</span>
                    <span style={{ fontSize: 12.5, fontWeight: 700, color: '#222' }}>{r.qty} {t('shop.assembly_instructions.unit_pcs')}</span>
                </div>
            ))}
        </div>
    );
};

const AssemblyInstructionsModal = ({ code, open, onClose }) => {
    const s = useStyles();
    const { t, i18n } = useTranslation();
    const [furniture, setFurniture] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [exporting, setExporting] = useState(false);
    const printRootRef = useRef(null);

    useEffect(() => {
        if (!open || !code) { setFurniture(null); setLoadError(null); return; }
        setFurniture(null);
        setLoadError(null);
        fetch(`${API_BASE}/furnitures/${code}`, { credentials: 'include' })
            .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
            .then(setFurniture)
            .catch(() => setLoadError(t('shop.assembly_instructions.load_error')));
    }, [open, code, t]);

    const settings = useMemo(() => (furniture ? { ...furniture.settings, cabinets: furniture.cabinets } : null), [furniture]);
    const combined = useMemo(() => buildCombinedGroup(furniture?.cabinets), [furniture?.cabinets]);
    const groups = useMemo(() => (combined ? [combined] : []), [combined]);
    const calc = useMemo(
        () => (furniture && settings ? calculatePrice(furniture.cabinets, settings) : null),
        [furniture, settings]
    );
    const lifestyleUrl = furniture?.lifestyleImagePath ? `${API_HOST}${furniture.lifestyleImagePath}` : null;

    const { images, capturing, hiddenNode, progress } = useFurnitureDrawings(open && !!settings, groups, settings || {});
    const ext = images['combined-exterior'] || {};
    const int = images['combined-interior'] || {};

    const dateStr = useMemo(() => new Date().toLocaleDateString(i18n.language === 'en' ? 'en-GB' : 'lt-LT'), [i18n.language]);
    const ready = !!settings && !capturing;

    const interiorItems = useMemo(() => buildInteriorItems(settings?.sectionOptions), [settings]);
    const stats = calc?.internalStats || {};
    const hardwareRows = calc ? [
        { icon: 'door', label: t('shop.assembly_instructions.hardware_doors'), qty: calc.doorPanels?.length || 0 },
        { icon: 'drawer', label: t('shop.assembly_instructions.hardware_drawers'), qty: stats.drawerCount || 0 },
        { icon: 'liftUp', label: t('shop.assembly_instructions.hardware_lift_up'), qty: stats.upDoorCount || 0 },
        { icon: 'liftDown', label: t('shop.assembly_instructions.hardware_lift_down'), qty: stats.downDoorCount || 0 },
        { icon: 'hanger', label: t('shop.assembly_instructions.hardware_hangers'), qty: stats.hangerCount || 0 },
        { icon: 'pullout', label: t('shop.assembly_instructions.hardware_pullouts'), qty: stats.pullOutCount || 0 },
        { icon: 'pantograph', label: t('shop.assembly_instructions.hardware_pantographs'), qty: stats.pantographCount || 0 },
        // calc.legCount visada apskaičiuojamas (žr. calculatePrice.js), bet
        // realiai naudojamas TIK kai pagrindo tipas yra "Kojelės" (6) — kitaip
        // (pvz. "Cokolis") rodyti kojelių skaičių būtų klaidinga.
        { icon: 'legs', label: t('shop.assembly_instructions.hardware_legs'), qty: settings?.basisType === 6 ? (calc.legCount || 0) : 0 },
    ].filter(r => r.qty > 0) : [];

    const stepContext = {
        hasShelves: (furniture?.cabinets || []).some(c => (c.shelves || []).length > 0),
        hasInteriorItems: interiorItems.length > 0,
        hasDrawers: (stats.drawerCount || 0) > 0,
        hasDoors: (calc?.doorPanels?.length || 0) > 0,
        hasBase: settings?.basisType > 0 && settings?.basisType !== 5, // 5 = "Be pagrindo"
        isTallOrWallMounted: (combined?.height || 0) >= 1.8,
    };
    const steps = ASSEMBLY_STEPS.filter(s => !s.when || s.when(stepContext));

    const TOTAL_PAGES = 5;

    const handleDownload = useCallback(async () => {
        if (!printRootRef.current || !ready) return;
        setExporting(true);
        try {
            await exportSummaryToPdf(printRootRef.current, `surinkimo-instrukcija-${code || 'baldas'}.pdf`);
        } finally {
            setExporting(false);
        }
    }, [ready, code]);

    if (!open) return null;

    // Rendered via portal directly under <body>: the public site's Header
    // (src/layout/Header.jsx) establishes its own stacking context on every
    // route, which otherwise paints over a plain in-tree `fixed inset-0`
    // overlay regardless of z-index — confirmed by DOM measurement during
    // testing. A portal sidesteps that entirely by making this a direct,
    // later DOM sibling.
    // BUG (rasta 2026-09-04, žr. 20 punktų sąrašo #8/#12): FluentProvider CSS
    // kintamieji galioja TIK jo DOM pomedžiui — createPortal į document.body
    // iš jo ištrūksta, tad visi tokens.* be šio apvyniojimo tyliai virsta
    // permatomi/undefined (žr. detalų komentarą theme/primitives.jsx SimpleModal).
    return createPortal((
        <FluentProvider theme={fititLightTheme} style={{ background: 'transparent' }}>
        <div className={s.root}>
            <div className={s.header}>
                <div className={s.iconChip}>
                    <ConstructionIcon style={{ fontSize: 20 }} />
                </div>
                <div className={s.titleWrap}>
                    <p className={s.title}>{t('shop.assembly_instructions.title')} {code ? `— ${code}` : ''}</p>
                    {capturing && <p className={s.progressText}>{t('shop.assembly_instructions.preparing', { done: progress.done, total: progress.total })}</p>}
                    {loadError && <p className={s.errorText}>{loadError}</p>}
                </div>
                <div className={s.headerActions}>
                    <Button
                        appearance="primary" onClick={handleDownload} disabled={!ready || exporting}
                        icon={exporting ? <Spinner size="tiny" appearance="inverted" /> : <DownloadIcon style={{ fontSize: 18 }} />}
                    >
                        {t('shop.assembly_instructions.download_pdf')}
                    </Button>
                    <Button appearance="subtle" icon={<DismissIcon style={{ fontSize: 20 }} />} onClick={onClose} aria-label={t('shop.assembly_instructions.close')} />
                </div>
            </div>

            {hiddenNode}

            <div className={s.body}>
                {!settings ? (
                    <div className={s.loadingWrap}>
                        <Spinner size="medium" />
                    </div>
                ) : (
                    <div ref={printRootRef}>
                        {/* ── Viršelis ── */}
                        <PageShell>
                            <TitleBlock code={code} pageLabel={t('shop.assembly_instructions.page_cover')} dateStr={dateStr} pageNum={1} totalPages={TOTAL_PAGES} />
                            <div style={{ fontSize: 26, fontWeight: 800, marginTop: 20, marginBottom: 8 }}>{t('shop.assembly_instructions.title')}</div>
                            <p style={{ fontSize: 13, color: '#555', lineHeight: 1.6, marginBottom: 18 }}>
                                {t('shop.assembly_instructions.cover_intro')}
                            </p>

                            {(lifestyleUrl || ext.img3d) && (
                                <div style={{ border: '1px solid #ddd', borderRadius: 8, overflow: 'hidden', background: '#fafafa', height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 18 }}>
                                    <img
                                        src={lifestyleUrl || ext.img3d}
                                        alt={code}
                                        style={{ maxWidth: '100%', maxHeight: '100%', width: lifestyleUrl ? '100%' : 'auto', height: lifestyleUrl ? '100%' : 'auto', objectFit: lifestyleUrl ? 'cover' : 'contain', display: 'block' }}
                                    />
                                </div>
                            )}

                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 28px', padding: '14px 16px', border: '1px solid #eee', borderRadius: 8, marginBottom: 22 }}>
                                {[
                                    [t('shop.assembly_instructions.stat_width'), `${((combined?.width || 0) * 100).toFixed(0)} cm`],
                                    [t('shop.assembly_instructions.stat_height'), `${((combined?.height || 0) * 100).toFixed(0)} cm`],
                                    [t('shop.assembly_instructions.stat_depth'), `${((combined?.depth || 0) * 100).toFixed(0)} cm`],
                                    [t('shop.assembly_instructions.stat_basis'), getBasisLabel(settings.basisType)],
                                    [t('shop.assembly_instructions.stat_quality'), t(`shop.assembly_instructions.${QUALITY_KEYS[settings.quality] || QUALITY_KEYS[0]}`)],
                                ].filter(([, v]) => v).map(([k, v]) => (
                                    <div key={k}>
                                        <div style={{ fontSize: 10.5, color: '#999', textTransform: 'uppercase', letterSpacing: 0.3 }}>{k}</div>
                                        <div style={{ fontSize: 13.5, fontWeight: 700 }}>{v}</div>
                                    </div>
                                ))}
                            </div>

                            <H2>{t('shop.assembly_instructions.toc_title')}</H2>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12.5, color: '#444' }}>
                                {[
                                    ['2', t('shop.assembly_instructions.toc_2')],
                                    ['3', t('shop.assembly_instructions.toc_3')],
                                    ['4', t('shop.assembly_instructions.toc_4')],
                                    ['5', t('shop.assembly_instructions.toc_5')],
                                ].map(([n, label]) => (
                                    <div key={n} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                        <span style={{ width: 20, height: 20, borderRadius: tokens.borderRadiusCircular, background: '#f0f0f0', color: '#555', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{n}</span>
                                        <span>{label}</span>
                                    </div>
                                ))}
                            </div>
                        </PageShell>

                        {/* ── Bendra informacija ── */}
                        <PageShell>
                            <TitleBlock code={code} pageLabel={t('shop.assembly_instructions.page_general')} dateStr={dateStr} pageNum={2} totalPages={TOTAL_PAGES} />
                            <DrawingPair img3d={ext.img3d} img2d={ext.img2d} dimensions={[combined?.width, combined?.height, combined?.depth]} />
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
                                <ThumbRow image={getBasisImage(settings.basisType)} title={t('shop.assembly_instructions.info_basis')} sub={getBasisLabel(settings.basisType)} />
                                <ThumbRow image={getEdgesImage(settings.edges)} title={t('shop.assembly_instructions.info_edges')} sub={getEdgesLabel(settings.edges)} />
                                <ThumbRow title={t('shop.assembly_instructions.info_quality')} sub={t(`shop.assembly_instructions.${QUALITY_KEYS[settings.quality] || QUALITY_KEYS[0]}`)} />
                                <ThumbRow title={t('shop.assembly_instructions.info_back_wall')} sub={settings.backWall ? t('shop.assembly_instructions.yes') : t('shop.assembly_instructions.no')} />
                            </div>
                        </PageShell>

                        {/* ── Surinkimo eiga ── */}
                        <PageShell>
                            <TitleBlock code={code} pageLabel={t('shop.assembly_instructions.page_steps')} dateStr={dateStr} pageNum={3} totalPages={TOTAL_PAGES} />
                            <H2>{t('shop.assembly_instructions.tools_title')}</H2>
                            <ToolsRow />
                            <H2>{t('shop.assembly_instructions.steps_title')}</H2>
                            <ol style={{ margin: 0, paddingLeft: 20, fontSize: 13.5, lineHeight: 2.2, color: '#333' }}>
                                {steps.map((step) => <li key={step.key}>{t(`shop.assembly_instructions.${step.key}`)}</li>)}
                            </ol>
                            <div style={{ marginTop: 20, padding: '12px 16px', border: '1px solid #fab219', borderRadius: 6, background: '#fff8e8', fontSize: 12.5, color: '#7a5900', lineHeight: 1.7 }}>
                                <strong>{t('shop.assembly_instructions.tips_title')}</strong>
                                <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                                    <li>{t('shop.assembly_instructions.tip_0')}</li>
                                    <li>{t('shop.assembly_instructions.tip_1')}</li>
                                    <li>{t('shop.assembly_instructions.tip_2')}</li>
                                </ul>
                            </div>
                            <div style={{ marginTop: 14, padding: '12px 16px', border: '1px solid #e34b4b', borderRadius: 6, background: '#fdecec', fontSize: 12.5, color: '#7a1f1f', lineHeight: 1.7 }}>
                                <strong>{t('shop.assembly_instructions.warning_title')}</strong> {t('shop.assembly_instructions.warning_intro')}
                                <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                                    {stepContext.isTallOrWallMounted && <li>{t('shop.assembly_instructions.warning_wall')}</li>}
                                    <li>{t('shop.assembly_instructions.warning_children')}</li>
                                    <li>{t('shop.assembly_instructions.warning_read')}</li>
                                </ul>
                            </div>
                        </PageShell>

                        {/* ── Fasadas / Durelės ── */}
                        <PageShell>
                            <TitleBlock code={code} pageLabel={t('shop.assembly_instructions.page_facade')} dateStr={dateStr} pageNum={4} totalPages={TOTAL_PAGES} />
                            <DrawingPair img3d={ext.img3d} img2d={ext.img2d} dimensions={[combined?.width, combined?.height, combined?.depth]} />
                            <H2>{t('shop.assembly_instructions.doors_title')}</H2>
                            {(!calc?.doorPanels || calc.doorPanels.length === 0) ? (
                                <p style={{ fontSize: 12.5, color: '#777' }}>{t('shop.assembly_instructions.doors_empty')}</p>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    {calc.doorPanels.map((d) => {
                                        const specificHandle = d.handle === 3 ? settings.specificHandles?.[d.groupId] : null;
                                        const groupEntry = (settings.mergedSections || []).find(s => s.groupId === d.groupId);
                                        const doorTypeValue = groupEntry
                                            ? settings.doorColors?.[`${groupEntry.furnitureId}-${groupEntry.cabinetIndex}-${groupEntry.sectionIndex}`]
                                            : null;
                                        return (
                                            <div key={d.groupId} style={{ border: '1px solid #eee', borderRadius: 6, padding: 12, display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'center' }}>
                                                <ThumbRow
                                                    image={resolvePlateImage(d.plate)}
                                                    title={t('shop.assembly_instructions.door_label', { material: d.material })}
                                                    sub={`${d.code || 'STD'} · ${getDoorTypeLabel(doorTypeValue)}`}
                                                />
                                                <ThumbRow
                                                    image={specificHandle ? resolveUploadImage(specificHandle) : getHandleTypeImage(d.handle)}
                                                    title={t('shop.assembly_instructions.handle_label')}
                                                    sub={specificHandle?.name || getHandleTypeLabel(d.handle)}
                                                />
                                                <span style={{ marginLeft: 'auto', fontSize: 11.5, color: '#777' }}>
                                                    {(d.width * 100).toFixed(0)} × {(d.height * 100).toFixed(0)} cm
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </PageShell>

                        {/* ── Vidus / Furnitūra ── */}
                        <PageShell>
                            <TitleBlock code={code} pageLabel={t('shop.assembly_instructions.page_interior')} dateStr={dateStr} pageNum={5} totalPages={TOTAL_PAGES} />
                            <DrawingPair img3d={int.img3d} img2d={int.img2d} dimensions={[combined?.width, combined?.height, combined?.depth]} />
                            <H2>{t('shop.assembly_instructions.interior_title')}</H2>
                            {interiorItems.length === 0 ? (
                                <p style={{ fontSize: 12.5, color: '#777', marginBottom: 20 }}>{t('shop.assembly_instructions.interior_empty')}</p>
                            ) : (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18, marginBottom: 20 }}>
                                    {interiorItems.map((item) => (
                                        <ThumbRow
                                            key={item.key}
                                            image={getInteriorItemImage(item.type)}
                                            title={getInteriorItemLabel(item.type)}
                                            sub={item.heightCm ? `${item.heightCm} cm` : undefined}
                                        />
                                    ))}
                                </div>
                            )}
                            <H2>{t('shop.assembly_instructions.hardware_title')}</H2>
                            {hardwareRows.length === 0 ? (
                                <p style={{ fontSize: 12.5, color: '#777' }}>{t('shop.assembly_instructions.hardware_empty')}</p>
                            ) : (
                                <HardwareList rows={hardwareRows} />
                            )}

                            <div style={{ marginTop: 22, padding: '14px 16px', border: `1px solid ${TEAL}55`, borderRadius: 8, background: '#f0faf8', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                                <div style={{ width: 36, height: 36, borderRadius: tokens.borderRadiusCircular, background: TEAL, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    <svg width="17" height="17" viewBox="0 0 24 24" fill="#fff">
                                        <path d="M6.6 10.8c1.4 2.8 3.8 5.2 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1v3.5c0 .6-.4 1-1 1C11.6 21.1 2.9 12.4 2.9 1.9c0-.6.4-1 1-1H7.4c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z" />
                                    </svg>
                                </div>
                                <div style={{ minWidth: 0 }}>
                                    <div style={{ fontSize: 13, fontWeight: 700, color: '#222' }}>{t('shop.assembly_instructions.help_title')}</div>
                                    <div style={{ fontSize: 12, color: '#555' }}>
                                        {t('shop.assembly_instructions.help_text')}: <strong>+370 605 17874</strong> · <strong>info@fitit.lt</strong>
                                    </div>
                                </div>
                            </div>
                        </PageShell>
                    </div>
                )}
            </div>
        </div>
        </FluentProvider>
    ), document.body);
};

export default AssemblyInstructionsModal;
